import React, { useState, useEffect } from 'react';
import { Award, Briefcase, CheckCircle, Globe, Layers, Plus, Target, Trash2, XCircle } from 'lucide-react';
import { api, API_URL } from '../api';

const PartnerAdminSetup = ({ user, onComplete, onSkip }) => {
  const [step, setStep] = useState(1); // 1: Welcome/About, 2: Add Trainers
  const [trainers, setTrainers] = useState([{ email: '', fullName: '', status: 'pending' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [aboutContent, setAboutContent] = useState(null);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const response = await fetch(`${API_URL}/about`);
        const data = await response.json();
        setAboutContent(data);
      } catch (err) {
        console.error('Failed to load about content:', err);
      }
    };
    fetchAbout();
  }, []);

  const addTrainer = () => {
    if (trainers.length < 4) { // Max 4 additional trainers (+ admin = 5)
      setTrainers([...trainers, { email: '', fullName: '', status: 'pending' }]);
    }
  };

  const removeTrainer = (index) => {
    const newTrainers = trainers.filter((_, i) => i !== index);
    setTrainers(newTrainers);
  };

  const updateTrainer = (index, field, value) => {
    const newTrainers = [...trainers];
    newTrainers[index] = { ...newTrainers[index], [field]: value };
    if (field === 'email') {
      const trimmed = value.trim();
      if (!trimmed) {
        newTrainers[index].status = 'pending';
      } else {
        const emailDomain = trimmed.includes('@') ? trimmed.split('@')[1].toLowerCase() : '';
        newTrainers[index].status = emailDomain === (user.domain || '').toLowerCase() ? 'valid' : 'invalid_domain';
        newTrainers[index].message = `Email must be from your organization domain (@${user.domain})`;
      }
    }
    setTrainers(newTrainers);
    if (!newTrainers.some(t => t.status === 'invalid_domain')) {
      setError('');
    }
  };

  const handleCompleteSetup = async () => {
    setError('');
    const enteredTrainers = trainers.filter(t => t.email.trim());
    if (enteredTrainers.some(t => t.status === 'invalid_domain')) {
      setError(`Some emails are not from your domain (${user.domain}). Please use only @${user.domain} emails.`);
      return;
    }
    setSubmitting(true);
    try {
      const validTrainers = enteredTrainers.map(t => ({ email: t.email.trim(), full_name: t.fullName || '' }));

      await api.request('/partner-admin/complete-setup', {
        method: 'POST',
        body: JSON.stringify({
          designated_trainers: validTrainers,
          usage_agreement_accepted: true // Terms already accepted via main Terms modal
        })
      });
      
      onComplete();
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  const iconMap = {
    award: Award,
    briefcase: Briefcase,
    target: Target,
    layers: Layers
  };

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-4" data-testid="partner-setup-wizard">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-levelup-dark text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Partner Admin Setup</h2>
              <p className="text-gray-300 text-sm">Step {step} of 2</p>
            </div>
            <div className="flex space-x-2">
              {[1, 2].map(s => (
                <div
                  key={s}
                  className={`w-3 h-3 rounded-full ${s === step ? 'bg-levelup-accent' : s < step ? 'bg-green-400' : 'bg-gray-500'}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Step 1: Welcome/About */}
        {step === 1 && (
          <div className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-levelup-dark mb-2">Welcome to Skilling In a Box!</h3>
              <p className="text-gray-600">
                As a Partner Admin from <span className="font-semibold">{user.organization}</span>, you'll be able to designate trainers and access analytics for your organization.
              </p>
            </div>

            {/* About Content Summary */}
            <div className="bg-levelup-light rounded-lg p-6 mb-6 max-h-[40vh] overflow-y-auto">
              <h4 className="font-bold text-levelup-dark mb-4 flex items-center">
                <Globe className="mr-2 text-levelup-accent" size={20} />
                {aboutContent?.content?.overview?.title || 'What is Skilling In a Box?'}
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                {aboutContent?.content?.overview?.description}
              </p>
              
              <h5 className="font-semibold text-levelup-dark mb-3">Skilling Pillars:</h5>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {aboutContent?.content?.pillars?.map((pillar, index) => {
                  const IconComponent = iconMap[pillar.icon] || Award;
                  return (
                    <div key={index} className="flex items-center text-sm">
                      <IconComponent className="text-levelup-accent mr-2" size={16} />
                      <span className="text-gray-700">{pillar.title}</span>
                    </div>
                  );
                })}
              </div>
              
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); /* Can add modal for full about */ }}
                className="text-levelup-teal text-sm hover:text-levelup-accent"
              >
                Learn more about the program →
              </a>
            </div>

            <div className="flex justify-between">
              <button
                onClick={onSkip}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
                data-testid="setup-skip-button"
              >
                Skip for now
              </button>
              <button
                onClick={() => setStep(2)}
                className="bg-levelup-accent text-levelup-darker px-8 py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover transition-all"
                data-testid="setup-next-button"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Add Trainers */}
        {step === 2 && (
          <div className="p-6">
            <h3 className="text-xl font-bold text-levelup-dark mb-2">Designate Your Trainers</h3>
            <p className="text-gray-600 text-sm mb-6">
              Add up to 5 trainers (including yourself) from your organization who can download content and schedule training.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {/* Admin (self) - Read only */}
            <div className="bg-levelup-light rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-levelup-accent flex items-center justify-center text-levelup-darker font-bold mr-3">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-levelup-dark">{user.email}</p>
                    <p className="text-sm text-gray-500">{user.full_name} (You - Partner Admin)</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  Admin
                </span>
              </div>
            </div>

            {/* Additional Trainers */}
            <div className="space-y-3 max-h-[25vh] overflow-y-auto">
              {trainers.map((trainer, index) => (
                <div key={index} className="border rounded-lg p-4" data-testid={`trainer-row-${index}`}>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium text-sm flex-shrink-0">
                      {index + 2}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        type="email"
                        placeholder={`trainer${index + 1}@${user.domain}`}
                        value={trainer.email}
                        onChange={(e) => updateTrainer(index, 'email', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
                        data-testid={`trainer-email-${index}`}
                      />
                      <input
                        type="text"
                        placeholder="Full Name (optional)"
                        value={trainer.fullName}
                        onChange={(e) => updateTrainer(index, 'fullName', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
                        data-testid={`trainer-name-${index}`}
                      />
                      {trainer.status === 'valid' && (
                        <p className="text-green-600 text-xs flex items-center">
                          <CheckCircle size={14} className="mr-1" /> Will receive an invitation email
                        </p>
                      )}
                      {trainer.status === 'invalid_domain' && (
                        <p className="text-red-600 text-xs flex items-center">
                          <XCircle size={14} className="mr-1" /> {trainer.message}
                        </p>
                      )}
                    </div>
                    {trainers.length > 1 && (
                      <button
                        onClick={() => removeTrainer(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                        data-testid={`remove-trainer-${index}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {trainers.length < 4 && (
              <button
                onClick={addTrainer}
                className="mt-3 flex items-center text-levelup-teal hover:text-levelup-accent text-sm"
                data-testid="add-trainer-button"
              >
                <Plus size={18} className="mr-1" /> Add another trainer
              </button>
            )}

            <div className="flex justify-between mt-6 pt-4 border-t">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Back
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={handleCompleteSetup}
                  disabled={submitting}
                  className="bg-levelup-accent text-levelup-darker px-8 py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="complete-setup-button"
                >
                  {submitting ? 'Completing...' : 'Complete Setup'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Training Partner: My Analytics page

export default PartnerAdminSetup;
